import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, useLang, API } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Target, Check, Sparkles, Loader2 } from 'lucide-react';
import { GEO_REGIONS, regionLabel } from '../lib/geographies';
import { countryLabel } from '../lib/localize';

const SECTORS = ['Energy', 'Climate', 'Infrastructure', 'Agriculture', 'Water', 'Transport', 'Health', 'Finance'];
const RISK = ['Low', 'Medium', 'High'];

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
      active ? 'bg-[#45B08F] border-[#45B08F] text-[#0A2540] font-medium' : 'border-[#285D71] text-slate-300 hover:border-[#45B08F]/60'
    }`}
  >
    {children}
  </button>
);

const OnboardingWizard = () => {
  const { user, loading, checkAuth } = useAuth();
  const { lang } = useLang();
  const isEn = lang !== 'fr';
  const [step, setStep] = useState(0);
  // Mandat de l'institution : ce que voient les AUTRES membres et ce qui
  // alimente les scores de compatibilite. Jusqu'ici il etait saisi par
  // l'administrateur et jamais confirme par l'institution elle-meme.
  const [mandat, setMandat] = useState(null);
  const [mandatModifie, setMandatModifie] = useState(false);
  // Toute edition marque le mandat comme modifie. Sans ce drapeau, un
  // collegue qui se contente de relire reecrirait la confirmation a son
  // nom, et une correction hative ecraserait un travail reflechi en silence.
  const majMandat = (fn) => { setMandatModifie(true); setMandat(fn); };
  useEffect(() => {
    const inst = user?.institution_id;
    if (!inst) return;                       // admin / secretariat : pas de mandat
    axios.get(`${API}/institutions/${inst}`)
      .then(({ data }) => setMandat({
        sectors: data.sectors || [],
        countries: (data.countries || []).join(', '),
        ticket_size_min: data.ticket_size_min ?? 0,
        ticket_size_max: data.ticket_size_max ?? 0,
        risk_appetite: data.risk_appetite || 'Medium',
        nom: data.name || inst,
        // Le mandat appartient a l'institution, pas au membre. Si un collegue
        // l'a deja confirme, on le dit et on ne le reecrit pas en silence.
        confirme_par: data.mandate_confirmed_by || null,
        confirme_le: data.mandate_confirmed_at || null,
      }))
      .catch(() => setMandat(null));         // en cas d'echec, l'etape disparait
  }, [user?.institution_id]);
  const [sectors, setSectors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [minM, setMinM] = useState('');
  const [maxM, setMaxM] = useState('');
  const [risk, setRisk] = useState([]);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const path = window.location.pathname;
  const ndaPending = user && !(user.nda_accepted_at && user.nda_version === user.current_nda_version);
  if (loading || !user || ndaPending || user.onboarded || path === '/login' || path.startsWith('/reset-password')) return null;

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const complete = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/users/me/onboarding-complete`, {});
      try { sessionStorage.setItem(`idfc_tour_seen_session_${user.user_id || user.email}`, 'true'); } catch (_) {}
      await checkAuth();
    } catch {
      toast.error(isEn ? 'Something went wrong' : 'Une erreur est survenue');
      setSaving(false);
    }
  };

  const finish = async (skip = false) => {
    setSaving(true);
    try {
      if (skip) {
        await axios.post(`${API}/users/me/onboarding-complete`, {});
        try { sessionStorage.setItem(`idfc_tour_seen_session_${user.user_id || user.email}`, 'true'); } catch (_) {}
        await checkAuth();
        return;
      }
      // Le mandat d'abord : c'est la donnee collective, celle qui engage
      // l'institution. Un echec ici ne doit pas empecher d'enregistrer les
      // preferences personnelles, d'ou le catch silencieux.
      // Deja confirme par un collegue et rien modifie ici : on ne reecrit
      // pas. Sinon la confirmation changerait de nom a chaque nouvel arrivant.
      if (mandat && (!mandat.confirme_par || mandatModifie)) {
        try {
          await axios.put(`${API}/institutions/${user.institution_id}`, {
            sectors: mandat.sectors,
            countries: mandat.countries.split(',').map((x) => x.trim()).filter(Boolean),
            ticket_size_min: Number(mandat.ticket_size_min) || 0,
            ticket_size_max: Number(mandat.ticket_size_max) || 0,
            risk_appetite: mandat.risk_appetite,
          });
        } catch { /* signale par le toast d'erreur general si besoin */ }
      }
      await axios.post(`${API}/alerts/preferences`, {
        sectors, countries,
        min_amount: minM ? Number(minM) * 1e6 : null,
        max_amount: maxM ? Number(maxM) * 1e6 : null,
        risk_levels: risk, stages: [], active: true,
      });
      let top = [];
      try {
        const res = await axios.get(`${API}/me/top-matches?limit=5`);
        top = res.data?.projects || [];
      } catch (_) { /* non-blocking */ }
      setMatches(top);
      setShowResults(true);
      setSaving(false);
    } catch {
      toast.error(isEn ? 'Something went wrong' : 'Une erreur est survenue');
      setSaving(false);
    }
  };

  const steps = [
    {
      title: isEn ? 'Which sectors do you invest in?' : 'Dans quels secteurs investissez-vous ?',
      sub: isEn ? 'Select all that apply' : 'Sélectionnez tout ce qui s\'applique',
      body: (
        <div className="flex flex-wrap gap-2" data-testid="onb-sectors">
          {SECTORS.map((s) => <Chip key={s} active={sectors.includes(s)} onClick={() => toggle(sectors, setSectors, s)} testId={`onb-sector-${s}`}>{s}</Chip>)}
        </div>
      ),
    },
    {
      title: isEn ? 'Which geographies?' : 'Quelles géographies ?',
      sub: isEn ? 'Countries you focus on' : 'Pays sur lesquels vous vous concentrez',
      body: (
        <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1" data-testid="onb-countries">
          {GEO_REGIONS.map((region) => (
            <div key={region.key}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{regionLabel(region, lang)}</div>
              <div className="flex flex-wrap gap-2">
                {region.countries.map((c) => (
                  <Chip key={c} active={countries.includes(c)} onClick={() => toggle(countries, setCountries, c)} testId={`onb-country-${c}`}>{countryLabel(c, lang)}</Chip>
                ))}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: isEn ? 'Your typical ticket size' : 'Votre taille de ticket habituelle',
      sub: isEn ? 'In USD millions (optional)' : 'En millions USD (optionnel)',
      body: (
        <div className="flex items-center gap-3" data-testid="onb-ticket">
          <div className="flex-1">
            <label className="text-xs text-slate-400">{isEn ? 'Min (M)' : 'Min (M)'}</label>
            <input type="number" value={minM} onChange={(e) => setMinM(e.target.value)} placeholder="10"
              className="w-full mt-1 bg-[#112C38] border border-[#285D71] rounded px-3 py-2 text-white text-sm" data-testid="onb-min" />
          </div>
          <span className="text-slate-500 mt-5">—</span>
          <div className="flex-1">
            <label className="text-xs text-slate-400">{isEn ? 'Max (M)' : 'Max (M)'}</label>
            <input type="number" value={maxM} onChange={(e) => setMaxM(e.target.value)} placeholder="200"
              className="w-full mt-1 bg-[#112C38] border border-[#285D71] rounded px-3 py-2 text-white text-sm" data-testid="onb-max" />
          </div>
        </div>
      ),
    },
    {
      title: isEn ? 'Your risk appetite' : 'Votre appétit au risque',
      sub: isEn ? 'Select all that apply' : 'Sélectionnez tout ce qui s\'applique',
      body: (
        <div className="flex flex-wrap gap-2" data-testid="onb-risk">
          {RISK.map((r) => <Chip key={r} active={risk.includes(r)} onClick={() => toggle(risk, setRisk, r)} testId={`onb-risk-${r}`}>{r}</Chip>)}
        </div>
      ),
    },
  ];

  if (mandat) {
    steps.push({
      title: isEn ? `Your institution's mandate — ${mandat.nom}` : `Le mandat de votre institution — ${mandat.nom}`,
      sub: mandat.confirme_par
        ? (isEn
            ? 'Already confirmed for your institution. Review it — change something only if it is wrong.'
            : "Déjà confirmé pour votre institution. Relisez — ne changez que si c'est inexact.")
        : (isEn
            ? 'This is what other members see, and what drives the matching scores. Confirm it or correct it.'
            : "C'est ce que voient les autres membres, et ce qui alimente les scores de compatibilité. Confirmez ou corrigez."),
      body: (
        <div className="space-y-4 max-h-[260px] overflow-y-auto pr-1" data-testid="onb-mandate">
          {mandat.confirme_par && (
            <div className="rounded-md border border-[#45B08F]/30 bg-[#45B08F]/10 px-3 py-2 text-[11px] text-[#9fd8c5]"
                 data-testid="onb-mandate-already">
              {isEn ? 'Confirmed by ' : 'Confirmé par '}
              <strong>{mandat.confirme_par}</strong>
              {mandat.confirme_le ? (isEn ? ' on ' : ' le ') + new Date(mandat.confirme_le).toLocaleDateString(isEn ? 'en-GB' : 'fr-FR') : ''}
              {isEn
                ? '. Your changes would replace theirs for the whole institution.'
                : ". Vos modifications remplaceraient les siennes pour toute l'institution."}
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{isEn ? 'Sectors financed' : 'Secteurs financés'}</div>
            <div className="flex flex-wrap gap-2">
              {SECTORS.map((sec) => (
                <Chip
                  key={sec}
                  active={mandat.sectors.includes(sec)}
                  onClick={() => majMandat((m) => ({ ...m, sectors: m.sectors.includes(sec) ? m.sectors.filter((x) => x !== sec) : [...m.sectors, sec] }))}
                  testId={`onb-mandate-sector-${sec}`}
                >{sec}</Chip>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{isEn ? 'Countries covered' : 'Pays couverts'}</div>
            <textarea
              value={mandat.countries}
              onChange={(e) => majMandat((m) => ({ ...m, countries: e.target.value }))}
              rows={2}
              className="w-full rounded-md bg-[#112C38] border border-[#285D71] text-slate-200 text-sm p-2"
              placeholder={isEn ? 'Senegal, Kenya, Ghana...' : 'Sénégal, Kenya, Ghana...'}
              data-testid="onb-mandate-countries"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{isEn ? 'Ticket min (USD m)' : 'Ticket min (M USD)'}</div>
              <input
                type="number" inputMode="numeric"
                value={Math.round((mandat.ticket_size_min || 0) / 1e6)}
                onChange={(e) => majMandat((m) => ({ ...m, ticket_size_min: (Number(e.target.value) || 0) * 1e6 }))}
                className="w-full rounded-md bg-[#112C38] border border-[#285D71] text-slate-200 text-sm p-2"
                data-testid="onb-mandate-min"
              />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{isEn ? 'Ticket max (USD m)' : 'Ticket max (M USD)'}</div>
              <input
                type="number" inputMode="numeric"
                value={Math.round((mandat.ticket_size_max || 0) / 1e6)}
                onChange={(e) => majMandat((m) => ({ ...m, ticket_size_max: (Number(e.target.value) || 0) * 1e6 }))}
                className="w-full rounded-md bg-[#112C38] border border-[#285D71] text-slate-200 text-sm p-2"
                data-testid="onb-mandate-max"
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{isEn ? 'Risk appetite' : 'Appétit au risque'}</div>
            <div className="flex flex-wrap gap-2">
              {RISK.map((r) => (
                <Chip key={r} active={mandat.risk_appetite === r}
                  onClick={() => majMandat((m) => ({ ...m, risk_appetite: r }))}
                  testId={`onb-mandate-risk-${r}`}>{r}</Chip>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            {isEn
              ? 'Modifiable at any time from the Marketplace. A change is recorded with your name and the date.'
              : 'Modifiable à tout moment depuis la Place de marché. Une modification est enregistrée à votre nom, avec la date.'}
          </p>
        </div>
      ),
    });
  }

  const isLast = step === steps.length - 1;
  const cur = steps[step];

  if (showResults) {
    const scoreColor = (s) => (s >= 80 ? 'text-[#45B08F]' : s >= 60 ? 'text-[#F5C544]' : 'text-slate-300');
    return (
      <div className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-results">
        <div className="w-full max-w-lg bg-[#0F3446] border border-[#285D71] rounded-xl shadow-2xl overflow-hidden">
          <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-[#12405A] to-[#0F3446] border-b border-[#285D71]">
            <div className="flex items-center gap-2 text-[#45B08F] mb-2">
              <Sparkles className="w-4 h-4" />
              <span className="text-[11px] uppercase tracking-widest font-semibold">{isEn ? 'Your mandate is set' : 'Votre mandat est défini'}</span>
            </div>
            <h2 className="text-xl font-semibold text-white">
              {matches.length
                ? (isEn ? `${matches.length} matches ready for you` : `${matches.length} correspondances pour vous`)
                : (isEn ? "You're all set" : 'Tout est prêt')}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {matches.length
                ? (isEn ? 'Scored against your mandate — highest fit first.' : 'Notées selon votre mandat — meilleure adéquation en premier.')
                : (isEn ? 'Publish or explore projects to see your matches populate.' : 'Publiez ou explorez des projets pour voir vos correspondances.')}
            </p>
          </div>

          <div className="px-6 py-5 max-h-[46vh] overflow-y-auto space-y-2">
            {matches.map((m, i) => (
              <div key={m.project_id} data-testid={`onb-match-${i}`}
                className="flex items-center gap-3 rounded-lg border border-[#285D71] bg-[#112C38] px-4 py-3">
                <div className={`text-lg font-bold tabular-nums ${scoreColor(m.score)}`}>{m.score}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white truncate">{(!isEn && m.title_fr) ? m.title_fr : m.title}</div>
                  <div className="text-xs text-slate-400 truncate">{m.country} · {m.sector}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="px-6 py-4 border-t border-[#285D71] flex justify-end">
            <Button onClick={complete} disabled={saving} className="bg-[#45B08F] hover:bg-[#3A9579] text-[#0A2540] font-medium gap-1.5" data-testid="onb-explore">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isEn ? 'Explore my matches' : 'Explorer mes correspondances'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="w-full max-w-lg bg-[#0F3446] border border-[#285D71] rounded-xl shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-[#12405A] to-[#0F3446] border-b border-[#285D71]">
          <div className="flex items-center gap-2 text-[#45B08F] mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-widest font-semibold">{isEn ? 'Welcome to IDFC' : 'Bienvenue sur IDFC'}</span>
          </div>
          <h2 className="text-xl font-semibold text-white">{isEn ? 'Set your investment criteria' : 'Définissez vos critères d\'investissement'}</h2>
          <p className="text-sm text-slate-400 mt-1">{isEn ? 'These criteria decide which projects you get alerted about — new matches, notifications and your weekly digest. Project scores work without them. Change anytime in your profile.' : 'Ces critères déterminent les projets dont vous serez alerté — correspondances, notifications et résumé hebdomadaire. Les scores des projets fonctionnent sans eux. Modifiable à tout moment dans votre profil.'}</p>
          <div className="flex gap-1.5 mt-4">
            {steps.map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-[#45B08F]' : 'bg-[#285D71]'}`} />)}
          </div>
        </div>

        <div className="px-6 py-6">
          <div className="flex items-start gap-2 mb-4">
            <Target className="w-5 h-5 text-[#F5C544] mt-0.5" />
            <div>
              <h3 className="text-white font-medium">{cur.title}</h3>
              <p className="text-xs text-slate-500">{cur.sub}</p>
            </div>
          </div>
          <div className="min-h-[96px]">{cur.body}</div>
        </div>

        <div className="px-6 py-4 border-t border-[#285D71] flex items-center justify-between">
          <button onClick={() => finish(true)} disabled={saving} className="text-sm text-slate-500 hover:text-slate-300" data-testid="onb-skip">
            {isEn ? 'Skip for now' : 'Passer pour l\'instant'}
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} disabled={saving} className="border-[#285D71] text-slate-300" data-testid="onb-back">
                {isEn ? 'Back' : 'Retour'}
              </Button>
            )}
            {!isLast ? (
              <Button onClick={() => setStep(step + 1)} disabled={step === 0 && sectors.length === 0} className="bg-[#45B08F] hover:bg-[#3A9579] text-[#0A2540] font-medium disabled:opacity-40" data-testid="onb-next">
                {isEn ? 'Next' : 'Suivant'}
              </Button>
            ) : (
              <Button onClick={() => finish(false)} disabled={saving} className="bg-[#45B08F] hover:bg-[#3A9579] text-[#0A2540] font-medium gap-1.5" data-testid="onb-finish">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {isEn ? 'Finish' : 'Terminer'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
